import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { getVendors, updateVendorApproval } from '../../services/woocommerce';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import { IconActivate, IconDeactivate, IconEye, IconReject } from '../../components/icons/icons';
import DynamicTable from '../../components/Table/DataTable';
import TablePaginationActions from '../../components/Table/tablePagination';


const VendorPage = () => {
  const [Vendor, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch vendors
  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await getVendors(page + 1, rowsPerPage, searchQuery); // searchQuery is server-side
      setVendors(response.data || []);
      const total = Number(response.headers['x-wp-total']) || response.data.length;
      setTotalCount(total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };


  // Call fetchVendors on page, rowsPerPage, or searchQuery change
  useEffect(() => {
    fetchVendors();
  }, [page, rowsPerPage, searchQuery]);

  // Update totalCount when vendor data changes
  useEffect(() => {
    setTotalCount(Vendor.length);
  }, [Vendor]);

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };
  const handleSearch = () => {
    setPage(0);
    setSearchQuery(searchInput);
  };

  // Filter vendors by search and status
  // Filter only by status, not search
  const filteredVendor = Vendor.filter((row) => {
    if (!statusFilter) return true;
    return statusFilter === 'active'
      ? row.disable_vendor === 'no'
      : row.disable_vendor === 'yes';
  });


  // Columns definition
  const columns = [
    {
      key: 'serial',
      label: 'S.No',
      align: 'center',
      render: (_row: any, index: number) => page * rowsPerPage + index + 1,
    },
    {
      key: 'vendor_display_name',
      label: 'Vendor Name',
      align: 'center',
      render: (row: any) => row.vendor_display_name || '-',
    },
    {
      key: 'vendor_shop_name',
      label: 'Shop Name',
      align: 'center',
      render: (row: any) => row.vendor_shop_name || '-',
    },
    {
      key: 'vendor_email',
      label: 'Email',
      align: 'center',
      render: (row: any) => row.vendor_email || '-',
    },
    {
      key: 'Contact',
      label: 'Contact No',
      align: 'center',
      render: (row: any) => row.vendor_phone || '-',
    },
    {
      key: 'Status',
      label: 'Status',
      align: 'center',
      render: (row: any) => {
        const isDisabled = row.disable_vendor === 'yes';
        const bgColor = isDisabled ? '#fee2e2' : '#d1fae5';
        const textColor = isDisabled ? '#991b1b' : '#065f46';
        const statusText = isDisabled ? 'Disabled' : 'Active';
        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: '4px 8px',
              borderRadius: '6px',
              fontWeight: 'bold',
              display: 'inline-block',
              minWidth: '90px',
              textAlign: 'center',
            }}
          >
            {statusText}
          </span>
        );
      },
    },
    {
      key: 'vendor_shop_logo',
      label: 'Logo',
      align: 'center',
      render: (row: any) => (
        <img
          src={row.vendor_shop_logo}
          alt="logo"
          className="h-10 w-10 rounded cursor-pointer"
          onClick={() => setPreviewImage(row.vendor_shop_logo)}
        />
      ),
    },
    {
      key: 'vendor_banner',
      label: 'Banner',
      align: 'center',
      render: (row: any) => (
        <img
          src={row.vendor_banner || row.vendor_list_banner}
          alt="banner"
          className="h-10 w-20 rounded cursor-pointer"
          onClick={() => setPreviewImage(row.vendor_banner || row.vendor_list_banner)}
        />
      ),
    },
  ];

  const handleShowDetails = (row: any) => {
    navigate(`/vendor/details/${row.vendor_id}`, {
      state: { item: row, fromTab: 'vendors', isButtonVisible: false, type: 'view' },
    });
  };

  const handleVendorAction = async (row: any, action: "Approve" | "Reject") => {
    console.log('row',row)
    if (!window.confirm(`Are you sure you want to ${action.toLowerCase()} ${row.formatted_display_name || row.name}?`)) return;

    const status = action === "Approve" ? "approve" : "reject";

    try {
      await updateVendorApproval(row.vendor_id, status);
      await fetchVendors();
    } catch (error: any) {
      alert(`Failed to ${action.toLowerCase()} vendor: ${error.message || "Unknown error"}`);
    }
  };
  const actions = [
    {
      label: 'View',
      color: '#2196f3',
      icon: <IconEye size={20} />,
      onClick: handleShowDetails,
    },
    // {
    //   label: "Approve",
    //   color: "#2e7d32",
    //   icon: <IconActivate size={20} />,
    //   onClick: (row: any) => handleVendorAction(row, "Approve"),
    // },
    // {
    //   label: "Reject",
    //   color: "#d32f2f",
    //   icon: <IconReject size={20} />,
    //   onClick: (row: any) => handleVendorAction(row, "Reject"),
    // },
  ];

  return (
    <Box p={2}>
      <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="flex-start">
        <div className="flex gap-2 items-end">
          <div className="flex-1 min-w-[250px]">
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="vendorName"
            >
              Vendor Name
            </label>
            <input
              type="text"
              placeholder="Search vendor name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              className="w-full px-3 py-2 border text-gray-700 border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            />
          </div>

          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-blue-300"
            disabled={!searchInput.trim()}
          >
            Search
          </button>

          <button
            onClick={() => {
              setSearchInput('');
              setSearchQuery('');
              setPage(0);
            }}
            className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600"
          >
            Clear
          </button>

        </div>

        <div className="flex-1 min-w-[180px]">
          <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status-select"
            value={statusFilter}
            onChange={handleStatusChange}
            className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Disabled</option>
          </select>
        </div>
      </Box>

      <DynamicTable
        columns={columns}
        data={filteredVendor}
        loading={loading}
        emptyMessage={"No vendors found"}
        actions={actions}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        ActionsComponent={TablePaginationActions}
      />

      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setPreviewImage(null)}
        >
          <div className="bg-white p-4 rounded shadow-lg">
            <img src={previewImage} alt="Preview" className="max-h-[80vh] max-w-[90vw] rounded" />
            <button
              onClick={() => setPreviewImage(null)}
              className="block mt-4 mx-auto px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </Box>
  );
};

export default VendorPage;
